/**
 * Motion preview presentation modes.
 *
 * VISTOERA_MOTION_RECONSTRUCTION_VIDEO_SKELETON_REAL_SOURCE_SYNCHRONIZED_PREVIEW_V2:
 * "Video + Skeleton" is now the canonical default (real source video +
 * skeleton overlay), replacing the retired synthetic Body silhouette.
 * "Skeleton" (standalone, no video) remains available as an explicit toggle
 * and as the fallback when no source video can be resolved/loaded.
 */

export type MotionPreviewMode = 'skeleton' | 'video_skeleton'

export const CANONICAL_MOTION_PREVIEW_MODE: MotionPreviewMode = 'video_skeleton'

export type NormalizeLegacyMotionPreviewModeOptions = {
  /** Whether a source video is resolvable/loadable for this node right now.
   * Defaults to `true` (canonical default applies) when omitted — callers
   * without a video concept (e.g. standalone Motion Asset node) should pass
   * `false` explicitly. */
  sourceVideoAvailable?: boolean
}

/** Safe hydration for legacy graph / localStorage values from the retired
 * synthetic body UI, plus the current `video_skeleton` / `skeleton` pair.
 * `body_skeleton` / `body_and_skeleton` (legacy synthetic body) hydrate to
 * `video_skeleton` when a source video is available, else `skeleton` —
 * never re-exposing the retired synthetic body (§21). */
export function normalizeLegacyMotionPreviewMode(
  raw: string | null | undefined,
  options?: NormalizeLegacyMotionPreviewModeOptions,
): MotionPreviewMode {
  const sourceVideoAvailable = options?.sourceVideoAvailable ?? true
  const value = String(raw || '').trim().toLowerCase()

  if (value === 'skeleton' || value === 'skeleton_only') return 'skeleton'
  if (value === 'video_skeleton') return sourceVideoAvailable ? 'video_skeleton' : 'skeleton'
  if (value === 'body_and_skeleton' || value === 'body_skeleton') {
    return sourceVideoAvailable ? 'video_skeleton' : 'skeleton'
  }
  // Unknown / empty → canonical default, gated by video availability.
  return sourceVideoAvailable ? CANONICAL_MOTION_PREVIEW_MODE : 'skeleton'
}

export type MotionPreviewViewportLayers = {
  /** Source video visual context layer — active now (was 'none' during the
   * synthetic-body-retirement preparation step). */
  visualContext: 'none' | 'source_video'
  /** Animated H36M skeleton overlay — always active for motion preview. */
  skeletonOverlay: 'active'
}

export const CURRENT_MOTION_PREVIEW_LAYERS: MotionPreviewViewportLayers = {
  visualContext: 'source_video',
  skeletonOverlay: 'active',
}
